<template>
	<div class="container-stock-browser-branch-list">
		<!-- Search Filter -->
		<BaseSearchFilter
			:active="isActive"
			:show="isActive"
			container-action-class="adjust-column"
		>
			<template #header>
				<CCol>
					<CInput
						v-model="query.keyword"
						:placeholder="$t('placeholder.stock_browser_sku')"
						@keyup.enter="onInputEnter"
					>
						<template #prepend-content>
							<FontAwesomeIcon :icon="['far', 'search']" />
						</template>
					</CInput>
				</CCol>
			</template>
			<template #action>
				<CDropdown
					:show.sync="adjustColumnButtonToggle"
					:color="adjustColumnButtonColor"
					toggler-text="Adjust column"
					class="container-adjust"
					block
				>
					<CContainer class="container-adjust-column p-0">
						<CRow class="m-0">
							<CCol class="typo-h5 pt-2 pl-3 mb-1" xl="12">
								Adjust channel columns
							</CCol>
							<CCol class="p-0" xl="12">
								<hr class="mt-2 mb-2">
							</CCol>
						</CRow>
						<CRow class="mt-2 ml-0 px-2">
							<CCol
								v-for="(channel, index) in transformChannels"
								:key="index"
								xl="12"
								class="mb-3"
							>
								<CInputCheckbox
									:checked="channel.checked"
									:label="channel.name"
									custom
									class="list-checkbox"
									@update:checked="handleCheckedAdjustColumn($event, channel.id)"
								/>
							</CCol>
						</CRow>
					</CContainer>
				</CDropdown>
			</template>
			<template #body>
				<CCol md="3">
					<BaseDropDown
						v-model="filter.brand"
						:options="brandOptions"
						:label-drop-down="$t('label.brand')"
						:placeholder="$t('message.selection')"
						:searchable="true"
						label="name"
						track-by="value"
						@input="handleBrandChange"
					/>
				</CCol>
				<CCol md="3">
					<BaseDropDown
						v-model="filter.status"
						:options="SKU_STATUS_OPTIONS"
						:label-drop-down="$t('label.productStatus')"
						:placeholder="$t('message.selection')"
						:searchable="false"
						label="name"
						track-by="value"
						@input="handleStatusChange"
					/>
				</CCol>
				<CCol md="3">
					<BaseDropDown
						v-model="filter.type"
						:options="SKU_TYPE_OPTIONS"
						:label-drop-down="$t('label.product_type')"
						:placeholder="$t('message.selection')"
						:searchable="false"
						label="name"
						track-by="value"
						@input="handleTypeChange"
					/>
				</CCol>
				<CCol md="3">
					<BaseDropDown
						v-model="filter.stock_rule"
						:options="stockRuleOptions"
						:label-drop-down="$t('label.stock_rule')"
						:placeholder="$t('message.selection')"
						:searchable="true"
						label="name"
						track-by="value"
						@input="handleStockRuleChange"
					/>
				</CCol>
				<CCol md="3">
					<BaseDropDown
						v-model="filter.channel_rule"
						:options="channelRuleOptions"
						:label-drop-down="$t('label.channel_rule')"
						:placeholder="$t('message.selection')"
						:searchable="true"
						label="name"
						track-by="value"
						@input="handleChannelRuleChange"
					/>
				</CCol>
			</template>
			<template #footer>
				<CCol md="8">
					{{ $t('message.sku_found', { number: totalCount }) }}
				</CCol>
				<CCol md="4">
					<button @click="onResetClick">
						{{ $t('button.resetFilters') }}
					</button>
				</CCol>
			</template>
		</BaseSearchFilter>

		<!-- Table, Pagination -->
		<BaseTable
			:is-loading="isLoading"
			:pagination="pagination"
			:fields="stockBrowserSkuTableFields"
			:items="dataTable"
			:hide-columns="['id']"
			:sort-columns="{
				sku: query.sort_by === 'sku' ? (query.sort_direction || 'asc') : 'asc',
				product_name: query.sort_by === 'product_name' ? (query.sort_direction || 'asc') : 'asc'
			}"
			:border-left="[
				'available-stock-cel',
				'reserved-cel',
				'status-cel',
				'group-a-remaining-cel',
				'channel-order-bnn-cel'
			]"
			:border-right="[
				'committed-cel',
				'group-b-remaining-cel',
			]"
			hover
			fixed
			clickable-rows
			name-route="StockBrowserSkuSingle"
			class="table-custom"
			@onSortClick="handleSortClick"
			@onPaginationClick="handlePageClick"
			@onRowClick="handleRowClick"
		>
			<template #no-items-view>
				<div class="empty-table-element">
					<p class="subtitle">
						{{ $t('message.notFoundSkuList') }}
					</p>
				</div>
			</template>
			<template #image="{ item = '' }">
				<img
					:src="item || '/img/product-placeholder.svg'"
					width="40"
					height="40"
				>
			</template>
			<template #status="{ item = '' }">
				<CBadge class="badge-sku-status" :color="item.color">
					{{ item.title }}
				</CBadge>
			</template>
		</BaseTable>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import { randomSearchString, cleanObject, cloneDeep, numberFormat, defaultValue } from '../helpers/utils';
import { getSelectedValue } from '../helpers/dropdown';
import { STOCK_BROWSER_SKU_TABLE_FIELDS, CHANNEL_ORDER_QTY, STOCK_BROWSER_TAB } from '../enums/stock';
import {
	SKU_STATUS_COLOR,
	SKU_STATUS_TITLE,
	SKU_STATUS_OPTIONS,
	SKU_TYPE_OPTIONS,
} from '../enums/sku';
import { ROUTER_NAME } from '../enums/router';

export default {
	name: 'StockBrowserSkuList',

	data() {
		return {
			STOCK_BROWSER_SKU_TABLE_FIELDS,
			SKU_STATUS_OPTIONS,
			SKU_TYPE_OPTIONS,
			STOCK_BROWSER_TAB,
			query: {
				keyword: this.$route.query.keyword || null,
				brand: this.$route.query.brand || null,
				status: this.$route.query.status || null,
				type: this.$route.query.type || null,
				stock_rule: this.$route.query.stock_rule || null,
				channel_rule: this.$route.query.channel_rule || null,
				sort_by: this.$route.query.sort_by || null,
				sort_direction: this.$route.query.sort_direction || null,
				page: this.$route.query.page || null,
				r: randomSearchString(),
			},
			filter: {
				brand: getSelectedValue(null, this.brandOptions),
				status: getSelectedValue(this.$route.query.status, SKU_STATUS_OPTIONS),
				type: getSelectedValue(this.$route.query.type, SKU_TYPE_OPTIONS),
				stock_rule: getSelectedValue(null, this.stockRuleOptions),
				channel_rule: getSelectedValue(null, this.channelRuleOptions),
			},
			adjustColumn: JSON.parse(localStorage.getItem('adjust_column')) || [], // channel ids
			adjustColumnButtonToggle: false,
		};
	},

	computed: {
		...mapState('stockBrowserSkuList', {
			data: 'data',
			isLoading: 'isLoading',
			pagination: 'pagination',
		}),
		...mapGetters('brandList', {
			brandOptions: 'brandOptions',
		}),
		...mapState('channelList', {
			channels: 'data',
			isLoadingChannels: 'isLoading',
		}),
		...mapGetters('stockRuleList', {
			stockRuleOptions: 'stockRuleOptions',
		}),
		...mapGetters('channelRuleList', {
			channelRuleOptions: 'channelRuleOptions',
		}),
		dataTable() {
			return this.data.map((item) => {
				const channels = (() => {
					return this.transformFields.reduce((acc, cur) => {
						const channelId = Number(cur.key.substring(8, cur.key.length));
						const value = item.channels.find((channel) => channel.id === channelId) || {};
						acc[cur.key] = defaultValue(value.quantity, '-');
						return acc;
					}, {});
				})();
				const channelOrder = this.transformChannelOrderField.reduce((acc, cur) => {
					const value = item.channelOrderQty || {};
					const keyValue = cur.key.replace('channel_order_', '');
					acc[cur.key] = defaultValue(value[keyValue], '0');
					return acc;
				}, {});
				return {
					id: item.id,
					image: item.image,
					sku: item.sku,
					product_name: item.product_name,
					brand: item.brand,
					available_stock: item.available_stock,
					online_stock: item.online_stock,
					offline_stock: item.offline_stock,
					total_stock: item.total_stock,
					reserved: item.stockCommitments.reserved,
					committed: item.stockCommitments.committed,
					online_a: item.group.online_a,
					online_b: item.group.online_b,
					online_a_remaining: item.group.online_a_remaining,
					online_b_remaining: item.group.online_b_remaining,
					...channels,
					...channelOrder,
					status: {
						title: SKU_STATUS_TITLE[item.status],
						color: SKU_STATUS_COLOR[item.status],
					},
					type: item.type,
					eolStatus: item.eolStatus ? 'On' : 'Off',
					last_synced: item.last_synced,
				};
			});
		},
		totalCount() {
			return numberFormat(this.pagination.total);
		},
		// check any dropdowns are activated
		isActive() {
			if (this.filter.brand?.value !== null) return true;
			if (this.filter.status?.value !== null) return true;
			if (this.filter.type?.value !== null) return true;
			if (this.filter.stock_rule?.value !== null) return true;
			if (this.filter.channel_rule?.value !== null) return true;

			return false;
		},
		adjustColumnButtonColor() {
			return this.adjustColumnButtonToggle ? 'secondary' : 'tertiary';
		},
		transformChannels() {
			return this.channels.map((channel) => {
				return {
					...channel,
					checked: this.adjustColumn.includes(channel.id),
				};
			});
		},
		transformFields() {
			return this.channels.reduce((acc, cur) => {
				if (this.adjustColumn.includes(cur.id)) {
					acc.push({
						key: `channel_${cur.id}`,
						label: cur.name,
						class: 'channel-col',
						cellClass: 'channel-cel',
					});
				}

				return acc;
			}, []);
		},
		channelOrder() {
			return this.data.reduce((acc, cur) => {
				return [...acc, ...Object.keys(cur.channelOrderQty)];
			}, []);
		},
		transformChannelOrderField() {
			if (this.channelOrder) {
				const channelOrderTotal = [...new Set(this.channelOrder)].reduce((acc, cur) => {
					return [...acc, {
						key: `channel_order_${cur}`,
						label: `Order Qty ${cur}`,
						class: `channel-order-${cur} th-mw-80`,
						cellClass: `channel-order-${cur}-cel`,
					}];
				}, []);
				if (channelOrderTotal.length) {
					const reoderChannelOrderTotal = channelOrderTotal.sort((a, b) => {
						if (b.key === CHANNEL_ORDER_QTY.TOTAL) {
							return -1;
						}
						return a.key.localeCompare(b.key);
					});
					return reoderChannelOrderTotal;
				}
			}
			return [];
		},
		stockBrowserSkuTableFields() {
			return STOCK_BROWSER_SKU_TABLE_FIELDS.reduce((acc, cur) => {
				acc.push(cur);

				if (cur.key === 'online_b_remaining') {
					acc.push(...this.transformFields);
					acc.push(...this.transformChannelOrderField);
				}

				return acc;
			}, []);
		},
	},

	async created() {
		if (this.brandOptions.length === 1) {
			await this.getBrands();
		}

		if (this.stockRuleOptions.length === 0) {
			await this.getStockRules({ perPage: 100000 });
		}

		if (this.channelRuleOptions.length === 0) {
			await this.getChannelRules({ perPage: 100000 });
		}
		const stockRuleCaseClubId = this.stockRuleOptions.length ? this.stockRuleOptions[0].value : null;
		const channelRuleCaseClubId = this.channelRuleOptions.length ? this.channelRuleOptions[0].value : null;
		if (this.$route.query.tab === STOCK_BROWSER_TAB.SKU) { // set route caseclub for tab sku
			if (channelRuleCaseClubId && isEmpty(this.$route.query.channel_rule)) {
				this.query.channel_rule = channelRuleCaseClubId;
				this.query.r = null;
				this.updateUrl();
			}
			if (stockRuleCaseClubId && isEmpty(this.$route.query.stock_rule)) {
				this.query.stock_rule = stockRuleCaseClubId;
				this.query.r = null;
				this.updateUrl();
			}
		}
		this.filter.brand = getSelectedValue(this.$route.query.brand ? parseInt(this.$route.query.brand, 10) : null, this.brandOptions);
		this.filter.stock_rule = getSelectedValue(this.$route.query.stock_rule ? parseInt(this.$route.query.stock_rule, 10) : null, this.stockRuleOptions);
		this.filter.channel_rule = getSelectedValue(this.$route.query.channel_rule ? parseInt(this.$route.query.channel_rule, 10) : null, this.channelRuleOptions);

		const isSetLocalStoreage = localStorage.getItem('adjust_column');
		if (!isSetLocalStoreage) {
			const channelIds = this.channels.map((channel) => channel.id);
			this.adjustColumn = channelIds;
			localStorage.setItem('adjust_column', JSON.stringify(channelIds));
		}
		await this.fetchData();
	},

	methods: {
		...mapActions('stockBrowserSkuList', {
			getProductStockSkus: 'getProductStockSkus',
		}),
		...mapActions('brandList', {
			getBrands: 'getBrands',
		}),
		...mapActions('channelList', {
			getChannels: 'getChannels',
		}),
		...mapActions('stockRuleList', {
			getStockRules: 'getStockRules',
		}),
		...mapActions('channelRuleList', {
			getChannelRules: 'getChannelRules',
		}),
		fetchData() {
			return Promise.all([
				this.getProductStockSkus(this.query),
				this.getChannels(),
			]);
		},
		onInputEnter(e) {
			this.query.page = null;
			this.query.keyword = e.target.value;
			this.updateUrl();
		},
		onResetClick() {
			this.query.keyword = null;
			this.query.brand = null;
			this.query.status = null;
			this.query.type = null;
			this.query.stock_rule = null;
			this.query.channel_rule = null;
			this.query.sort_by = null;
			this.query.sort_direction = null;
			this.query.page = null;
			this.query.r = null;
			this.updateUrl();
		},
		handleBrandChange(option) {
			this.query.brand = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
		handleStatusChange(option) {
			this.query.status = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
		handleTypeChange(option) {
			this.query.type = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
		handleStockRuleChange(option) {
			this.query.stock_rule = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
		handleChannelRuleChange(option) {
			this.query.channel_rule = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
		handleRowClick(id) {
			this.$router.push({ name: ROUTER_NAME.STOCK_BROWSER_SKU_SINGLE, params: { id } });
		},
		handleSortClick(key = '', state = false) {
			this.query.page = null;
			this.query.sort_by = key;
			this.query.sort_direction = state;
			this.updateUrl();
		},
		handlePageClick(pageNum) {
			this.query.page = pageNum;
			this.updateUrl();
		},
		updateUrl() {
			const query = cleanObject({
				// always put tab, page queries to the `newQuery`
				tab: this.$route.query.tab,
				...cloneDeep(this.query),
			});
			this.$router.push({ query }).catch(() => {});
		},
		handleCheckedAdjustColumn(isChecked, channelId) {
			const add = (value) => {
				this.adjustColumn.push(value);
			};
			const remove = (value) => {
				const index = this.adjustColumn.findIndex((id) => id === value);
				const found = index >= 0;
				if (found) {
					this.adjustColumn.splice(index, 1);
				}
			};

			if (isChecked) {
				add(channelId);
			} else {
				remove(channelId);
			}

			localStorage.setItem('adjust_column', JSON.stringify(this.adjustColumn));
		},
	},
};
</script>

<style lang="scss" scoped>
	::v-deep .table-custom {
		margin-top: rem(32);

		.badge-sku-status {
			min-width: rem(50);
		}

		.image {
			// .image-col, .image-cel
			&-col, &-cel {
				width: rem(40);
			}

			// .image-cel
			&-cel {
				padding-right: 0;
				padding-left: 0;
			}
		}

		.sku {
			// .sku-col
			&-col {
				min-width: rem(200 + $table-cell-padding);
			}
		}

		.product-name {
			// .product-name-col
			&-col {
				min-width: rem(300 + $table-cell-padding);
			}
		}

		.status, .type {
			// .status-col, .type-col
			&-col {
				min-width: rem(100 + $table-cell-padding);
			}
		}

		.brand {
			// .brand-col
			&-col {
				min-width: rem(180 + $table-cell-padding);
			}
		}

		.available-stock, .online-stock, .offline-stock, .reserved, .committed, .channel, .group {
			// .available-stock-col, .online-stock-col, .offline-stock-col, .reserved-col, .committed-col, .channel-col
			&-col {
				min-width: rem(120 + $table-cell-padding);
			}
		}

		.last-synced {
			// .last-synced-col
			&-col {
				min-width: rem(150 + $table-cell-padding);
			}
		}

		[class|="channel-order"] {
			text-transform: capitalize;
		}
	}

	.container-adjust {
		&-column {
			width: 280px;
		}
	}

	.container-stock-browser-branch-list ::v-deep .dropdown-menu {
		top: rem(8) !important;
	}

	.container-stock-browser-branch-list ::v-deep .dropdown-toggle::after {
		display: none !important;
	}
</style>
